import { DateValue } from "@v2/components/calendar/types";
import { differenceInDays, format, isToday, startOfDay } from "date-fns";

type OutFmt = "ymd" | "dmy" | "mdy";

export function getFormats(outputFormat: OutFmt, showDayName = false) {
  switch (outputFormat) {
    case "ymd":
      return {
        range: "yyyy-M-d",
        single: `${showDayName ? "EE " : ""}yyyy-M-d`
      };
    case "dmy":
      return {
        range: "dd/MM",
        single: `${showDayName ? "EE " : ""}d/M/yyyy`
      };
    default:
      return {
        range: "MM/dd",
        single: `${showDayName ? "EE " : ""}M/d/yyyy`
      };
  }
}

export function splitDatesIntoIntervals(
  dates: DateValue | undefined,
  outputFormat: OutFmt = "mdy",
  showDayName?: boolean
): ([string, string] | [string])[] {
  const intervals: ([string, string] | [string])[] = [];
  if (!dates) return [];

  const { range: rangeFmt, single: singleFmt } = getFormats(
    outputFormat,
    showDayName
  );

  if (dates instanceof Date) {
    return [[isToday(dates) ? "Today" : format(dates, singleFmt)]];
  }

  if (!Array.isArray(dates)) {
    if (!dates.from || !dates.to) return [];
    // check if both from and to are today and equal
    if (
      isToday(dates.from) &&
      isToday(dates.to) &&
      startOfDay(dates.from).getTime() === startOfDay(dates.to).getTime()
    ) {
      return [["Today"]];
    }
    return [[format(dates.from, rangeFmt), format(dates.to, rangeFmt)]];
  }

  if (dates.length === 0) return [];
  if (dates.length === 1) {
    return [[isToday(dates[0]) ? "Today" : format(dates[0], singleFmt)]];
  }

  let start = format(dates[0], rangeFmt);
  let previous = startOfDay(dates[0]);

  for (let i = 1; i < dates.length; i++) {
    const current = startOfDay(dates[i]);

    if (differenceInDays(current, previous) > 1) {
      if (start !== format(previous, rangeFmt)) {
        intervals.push([start, format(previous, rangeFmt)]);
      } else {
        intervals.push([start]);
      }
      start = format(current, rangeFmt);
    }
    previous = current;
  }

  if (start !== format(previous, rangeFmt)) {
    intervals.push([start, format(previous, rangeFmt)]);
  } else {
    intervals.push([start]);
  }

  return intervals;
}
